// Auto-revalidate the public site after a CMS save.
//
// Every save handler in the CMS calls `revalidatePublic(paths)` after a
// successful Firestore write. The manual sidebar "Refresh public site"
// button stays as a safety net — but under normal use the public site
// updates itself and users never need to think about caching.
//
// Design:
//   • Fire-and-forget. A failed revalidate never breaks a save — the
//     write already succeeded and the next ISR window (1h) will catch
//     the change. The console warning is enough of a signal for us.
//   • `type: "layout"` is passed through so a save on /blogs also busts
//     /blogs/[slug] (the detail routes are dynamic children of the list
//     layout in Next.js). Without this, editing a blog would leave the
//     detail page stale for up to an hour.
//   • Paths are batched into one POST — a single Vercel function
//     invocation per save, no matter how many segments need to bust.
//
// Add a new content type: append to PATHS_FOR below.

import { auth } from "../../Firebase/Firebase.utils";

const REVALIDATE_URL = process.env.REACT_APP_REVALIDATE_URL;

/**
 * Authorization header for nextjsprojv1's staff-only endpoints
 * (/api/revalidate, /api/rss-search): the signed-in user's Firebase ID
 * token, which the website verifies server-side. Replaces the shared
 * REACT_APP_*_SECRET values, which shipped inside this app's JavaScript
 * bundle and so were readable by anyone. getIdToken() returns a cached
 * token and refreshes it automatically when it's near expiry.
 */
export async function staffAuthHeaders() {
  const user = auth.currentUser;
  if (!user) return {};
  try {
    return { Authorization: `Bearer ${await user.getIdToken()}` };
  } catch (err) {
    console.warn("[staffAuthHeaders] Could not get ID token:", err?.message || err);
    return {};
  }
}

/**
 * Firestore collection or CMS resource key → array of public paths that
 * should bust when a doc in that collection is written.
 *
 * Rules of thumb:
 *   • Include the LISTING path (e.g. /blogs). Layout-type revalidate
 *     also handles the detail routes underneath.
 *   • For content that appears on multiple public pages (leaders show
 *     in /aboutUs AND /executive-leadership), list all of them.
 */
// "/sitemap.xml" is included for every collection that has public detail
// URLs, so new or unpublished pages show up in the sitemap right away.
export const PATHS_FOR = {
  blogs: ["/blogs", "/sitemap.xml"],
  news: ["/news", "/sitemap.xml"],
  newsRss: ["/news"],
  insights: ["/market-insights", "/sitemap.xml"],
  careers: ["/careers", "/sitemap.xml"],
  // Leaders show in the About Us leadership collage AND in the standalone
  // /executive-leadership grid + detail pages.
  leaders: ["/executive-leadership", "/aboutUs"],
  // Brokers only appear in the About Us slideshow. No detail routes.
  brokers: ["/aboutUs"],
  // Office buildings on /offices and /offices/[slug], plus the exclusive
  // buildings featured on the homepage and Landlord Representation.
  masterlist: ["/offices", "/", "/landlord-representation", "/sitemap.xml"],
  // For-sale listings power /property-search and /for-sale-details/[slug].
  listings: ["/property-search", "/for-sale-details", "/sitemap.xml"],
};

/**
 * Fire-and-forget revalidate. Callers do NOT await this — a slow or
 * failed revalidate must not delay the toast confirming the save.
 *
 * @param {string[]} paths - Public paths to bust. Empty array is a no-op.
 * @returns {Promise<void>} - Always resolves; errors are logged only.
 */
export async function revalidatePublic(paths) {
  if (!Array.isArray(paths) || paths.length === 0) return;
  if (!REVALIDATE_URL) {
    console.warn(
      "[revalidatePublic] Skipped — REACT_APP_REVALIDATE_URL is not set.",
    );
    return;
  }
  try {
    const res = await fetch(REVALIDATE_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(await staffAuthHeaders()),
      },
      body: JSON.stringify({ paths, type: "layout" }),
    });
    if (!res.ok) {
      console.warn(
        `[revalidatePublic] ${paths.join(", ")} returned HTTP ${res.status}`,
      );
    }
  } catch (err) {
    console.warn("[revalidatePublic] Network error:", err?.message || err);
  }
}

/**
 * Convenience wrapper: revalidate every public path known to be
 * affected by a write to `collectionKey`. Silently no-ops if the key
 * has no mapping (e.g. an internal-only collection).
 *
 * Usage:
 *   await setDocData("blogs", id, payload);
 *   revalidateFor("blogs");   // fire-and-forget, no await
 */
export function revalidateFor(collectionKey) {
  const paths = PATHS_FOR[collectionKey];
  if (paths) revalidatePublic(paths);
}
